import { cn } from "@/shared/lib/utils";
import * as React from "react";
import Typography from "@/shared/ui/Typography";
import { NavLink, useLocation } from "react-router-dom";

import Logo from "@/assets/icons/logo.svg?react";
import Close from "@/assets/icons/menu/close.svg?react";
import Menu from "@/assets/icons/menu/burger.svg?react";
import Telegram from "@/assets/icons/social/telegram.svg?react";
import Whatsapp from "@/assets/icons/social/whatsapp.svg?react";
import Max from "@/assets/icons/social/max.svg?react";
import Phone from "@/assets/icons/phone/black.svg?react";
import { CookieBanner } from "@/features/cookieConsent/ui/CookieBanner";
import { useCookieConsent } from "@/features/cookieConsent/model/useCookieConsent";
import { useHeaderFixed } from "@/shared/hooks/useScrollPosition";
import type { ContactsType } from "@/entities/site_data/model/types";
import PhoneButton from "@/shared/ui/buttons/PhoneButton";

export interface IHeaderProps extends React.ComponentPropsWithoutRef<"header"> {
    contacts?: ContactsType;
    className?: string;
}

const PHONE_NUMBER = "+7(123)45–61–96";

const NAV_ITEMS = [
    { to: "/", label: "главная" },
    { to: "/catalog", label: "каталог объектов" },
    { to: "/mortgage", label: "ипотека" },
] as const;

const NavItem: React.FC<{ to: string; label: string }> = ({ to, label }) => (
    <li>
        <NavLink to={to}>
            {({ isActive }) => (
                <Typography
                    className={cn(
                        "uppercase font-bold text-[14px] md:text-[14px] transition-colors",
                        isActive ? "text-blue-70" : "text-blue"
                    )}
                >
                    {label}
                </Typography>
            )}
        </NavLink>
    </li>
);

const Header = React.forwardRef<HTMLElement, IHeaderProps>(
    ({ className, contacts }, ref) => {
        const [menuOpened, setMenuOpened] = React.useState<boolean>(false);
        const { pathname } = useLocation();
        const path = pathname.split("/")[1];
        const isFixed = useHeaderFixed(32);

        const toggleMenu = React.useCallback(() => {
            setMenuOpened((prev) => !prev);
        }, []);

        const closeMenu = React.useCallback(() => {
            setMenuOpened(false);
        }, []);

        const { visible, handleVisible } = useCookieConsent();

        // Закрытие меню при изменении размера экрана
        React.useEffect(() => {
            const handleResize = () => {
                if (window.innerWidth >= 1024 && menuOpened) {
                    closeMenu();
                }
            };

            window.addEventListener("resize", handleResize);
            return () => window.removeEventListener("resize", handleResize);
        }, [menuOpened, closeMenu]);

        const bgColor =
            path == "" || path == "project"
                ? "bg-white-50 shadow-glass"
                : "bg-light-blue";

        const isRelative = path == "not-found";

        return (
            <header
                id="header_id"
                ref={ref}
                className={cn(
                    "absolute w-full left-0 top-0 z-20 pb-[4px] md:pb-[12px] flex flex-col rounded-b-[10px]",
                    isFixed && "fixed",
                    isRelative && "relative",
                    bgColor,
                    className
                )}
            >
                <CookieBanner visible={visible} handleAccept={handleVisible} />

                <div className="container w-full h-[40px] mt-[4px] md:mt-[12px] flex items-center justify-between">
                    <NavLink to={"/"}>
                        <Logo className="w-[141px] h-[22px]" />
                    </NavLink>

                    <nav>
                        <ul className="flex-row gap-[20px] hidden lg:flex">
                            {NAV_ITEMS.map((item) => (
                                <NavItem
                                    key={item.to}
                                    to={item.to}
                                    label={item.label}
                                />
                            ))}
                        </ul>
                    </nav>

                    <div className="flex flex-row items-center gap-[8px] md:gap-[10px]">
                        <a href={contacts?.telegramUrl} target="_blank">
                            <Telegram className="w-[31px] h-[30px] md:w-[36px] md:h-[35px] cursor-pointer transition-opacity" />
                        </a>
                        <a href={contacts?.whatsappUrl} target="_blank">
                            <Whatsapp className="w-[31px] h-[30px] md:w-[36px] md:h-[35px] cursor-pointer transition-opacity" />
                        </a>
                        <a href={contacts?.maxUrl} target="_blank">
                            <Max className="w-[30px] h-[30px] md:w-[35px] md:h-[35px] cursor-pointer transition-opacity" />
                        </a>

                        <a
                            href={`tel:${contacts?.phone}`}
                            className="hidden md:flex"
                        >
                            <PhoneButton
                                className="h-[39px]"
                                aria-label="Позвонить"
                            >
                                <Phone className="w-[23px] h-[23px]" />
                                {PHONE_NUMBER}
                            </PhoneButton>
                        </a>

                        <a
                            href={`tel:${contacts?.phone}`}
                            className="md:hidden flex"
                        >
                            <PhoneButton
                                className="h-[30px] w-[30px] p-0 justify-center"
                                aria-label="Позвонить"
                            >
                                <Phone className="w-[14px] h-[32px]" />
                            </PhoneButton>
                        </a>
                    </div>

                    <button
                        onClick={toggleMenu}
                        className="lg:hidden transition-opacity"
                        aria-label={
                            menuOpened ? "Закрыть меню" : "Открыть меню"
                        }
                        aria-expanded={menuOpened}
                    >
                        {menuOpened ? (
                            <Close className="w-[40px] h-[40px]" />
                        ) : (
                            <Menu className="w-[40px] h-[40px]" />
                        )}
                    </button>
                </div>

                <div
                    className={cn(
                        "absolute lg:hidden w-full top-[calc(100%+4px)] rounded-[10px] overflow-hidden transition-all duration-100 ease-in-out",
                        bgColor,
                        menuOpened
                            ? "max-h-[500px] opacity-100 pointer-events-auto"
                            : "max-h-0 opacity-0 pointer-events-none"
                    )}
                >
                    <div className="flex flex-col items-center gap-[20px] p-[32px] md:py-[20px]">
                        <nav>
                            <ul className="flex-row gap-[20px] flex justify-center">
                                {NAV_ITEMS.map((item) => (
                                    <NavItem
                                        key={item.to}
                                        to={item.to}
                                        label={item.label}
                                    />
                                ))}
                            </ul>
                        </nav>
                        <a
                            href={`tel:${contacts?.phone}`}
                            className="md:hidden inline"
                        >
                            <PhoneButton
                                className="h-[39px]"
                                aria-label="Позвонить"
                            >
                                <Phone className="w-[23px] h-[23px]" />
                                {PHONE_NUMBER}
                            </PhoneButton>
                        </a>
                    </div>
                </div>
            </header>
        );
    }
);

Header.displayName = "Header";
export default Header;
